import { useCallback, useRef } from 'react';
import { BlipChat } from 'blip-chat-widget';
import {
  destroyWidget,
  dispatchEnterEvent,
  dispatchLeaveEvent,
  formatLocaleBlipStyle,
  renderWidget,
} from 'pages/chat/methods';
import { EVENTS } from 'common/constants';
import useEventListener from 'common/hooks/useEventListener';
import { AccountsInfo } from 'models';

type UserInfo = Partial<AccountsInfo>;
type Params = UserInfo & {
  userId: number;
  userAccount: Record<string, unknown>;
  locale: string;
  withButton?: { color: string; icon: string };
};

type useRenderBlipChatReturn = {
  renderBlipChatWidget: (appKey: string) => void;
  blipChat: BlipChat;
};

const useRenderBlipChat = (props: Params): useRenderBlipChatReturn => {
  const blipChat = useRef<BlipChat>(new BlipChat());
  const { userId, userAccount, locale: appLocale, withButton } = props;
  const locale = formatLocaleBlipStyle(appLocale);

  const renderBlipChatWidget = useCallback(
    (appKey: string) => {
      if (document.getElementById('blip-chat-container')) {
        return; // Skip if already injected
      }

      blipChat.current
        .withAppKey(appKey)
        .withButton({ color: '#053D4E', icon: '', ...withButton })
        .withCustomCommonUrl('https://hotmart.chat.blip.ai/')
        .withCustomStyle('textarea#msg-textarea::placeholder { color: transparent; }')
        .withAccount(!userId ? { culture: locale } : userAccount)
        .withEventHandler(BlipChat.ENTER_EVENT, dispatchEnterEvent)
        .withEventHandler(BlipChat.LEAVE_EVENT, dispatchLeaveEvent);

      if (userId) {
        blipChat.current = blipChat.current.withAuth({
          authType: BlipChat.DEV_AUTH,
          userIdentity: userId,
          userPassword: userId,
        });
      }

      renderWidget(appKey, blipChat.current);
    },
    [userId, userAccount]
  );

  useEventListener(EVENTS.VULCANO_LOGOUT, () => destroyWidget(blipChat.current));

  return { renderBlipChatWidget, blipChat };
};

export default useRenderBlipChat;
